import { createSelector } from '@ngrx/store';
import {
  AccountTypes,
  DecedentInfoState,
  ISelectedClientAccountsState,
  TransactionTypesEnum,
} from '@shared/models';
import { getOpenAccountState, TransactionState } from '@store/reducers';
import { BeneficiariesState } from '@store/reducers/beneficiaries/beneficiaries.reducer';
import { IDocumentUploadState } from '@store/reducers/document-upload/document-upload.reducer';
import { EstateState } from '@store/reducers/estate/estate.reducer';
import { MfePersonalInfoClientState } from '@store/reducers/mfe-personal-info-client/mfe-personal-info-client.reducer';
import { IOpenAccountState } from '@store/reducers/open-account/open-account.reducer';
import { ITaxDocumentUploadState } from '@store/reducers/tax-document-upload/tax-document-upload.reducer';
import { TCFTrustState } from '@store/reducers/tcf-trust/tcf-trust.reducer';
import {
  getIsMultiInheritanceFlow,
  getIsTrustToTrustFlow,
  TriageState,
} from '@store/reducers/triage/triage.reducer';
import { TrustState } from '@store/reducers/trust/trust.reducer';
import { TrustTaxInfoState } from '@store/reducers/trust-tax-info/trust-tax-info.reducer';
import { selectBeneficiaryState } from '@store/selectors/beneficiaries/beneficiaries.selectors';
import {
  selectDecedentFullName,
  selectDecedentInfoState,
  selectDecedentInheritanceWaiverState,
} from '@store/selectors/decedent-info/decedent-info.selectors';
import { selectDocumentUploadState } from '@store/selectors/document-upload/document-upload.selectors';
import { selectEstateState } from '@store/selectors/estate/estate.selectors';
import {
  selectInheritanceIndividualJointToTrustToggle,
  selectInheritanceNewTermsToggle,
  selectInheritanceVCDToggle,
  selectMultiInheritanceEstateToggle,
} from '@store/selectors/feature/feature.selectors';
import { selectMfePersonalInfoClientPersonalInfo } from '@store/selectors/mfe-personal-info-client/mfe-personal-info-client.selectors';
import {
  selectedAccountsFrom,
  selectedClientAccountsState,
  selectTransactionType,
} from '@store/selectors/selected-client-accounts/selected-client-accounts.selectors';
import { selectTaxDocumentUploadState } from '@store/selectors/tax-document-upload/tax-document-upload.selectors';
import { selectHasClientUploadedL8File } from '@store/selectors/tax-document-upload/tax-document-upload.selectors';
import { selectTCFTrustState } from '@store/selectors/tcf-trust/tcf-trust.selectors';
import { selectCurrentTransaction } from '@store/selectors/transaction-state/transaction-state.selectors';
import {
  selectAccountType,
  selectIsProspect,
  selectTriageState,
} from '@store/selectors/triage/triage.selectors';
import { selectTrustState } from '@store/selectors/trust/trust.selectors';
import { selectTrustTaxInfoState } from '@store/selectors/trust-tax-info/trust-tax-info.selector';

import {
  buildAccountFromNonretirement,
  buildAccountToMultiInheritance,
  buildAccountToNonretirement,
} from '../cashier-nonretirement';
import {
  selectEmploymentReceipt,
  selectFinancialProfileReceipt,
  selectProfileReceipt,
} from '../new-account/new-account.selectors';
import {
  buildAccountFromTrust,
  buildAccountToTrust,
  buildDecedentInfoReceipt,
  buildDocumentUploadReceipt,
  buildEstateReceipt,
  buildFundingReceipt,
  buildTrusteeReceipt,
  buildTrustInfoReceipt,
} from './cashier-inheritance';
import {
  buildAccountFromIRA,
  buildAccountToIRA,
  buildBeneByRelationshipReceipt,
  buildDefaultBeneficiaryReceipt,
} from './cashier-ira';

export const selectDecedentInfoReceipt = (editable: boolean) =>
  createSelector(
    selectDecedentInfoState,
    selectTriageState,
    (state: DecedentInfoState, triage: TriageState) =>
      buildDecedentInfoReceipt(state, triage, editable),
  );

export const selectDocumentUploadReceipt = (editable: boolean = true) =>
  createSelector(
    selectDocumentUploadState,
    selectTaxDocumentUploadState,
    selectDecedentInheritanceWaiverState,
    selectCurrentTransaction,
    selectHasClientUploadedL8File,
    (
      documentUploadState: IDocumentUploadState,
      taxDocumentUploadState: ITaxDocumentUploadState,
      inheritanceWaiverState: string,
      currentTransactionState,
      hasClientUploadedL8File,
    ) =>
      buildDocumentUploadReceipt(
        documentUploadState,
        taxDocumentUploadState,
        inheritanceWaiverState,
        currentTransactionState,
        editable,
        hasClientUploadedL8File?.isL8ITWUploaded ?? false,
      ),
  );

export const selectEstateReceipt = (editable: boolean) =>
  createSelector(
    selectEstateState,
    selectTriageState,
    selectInheritanceNewTermsToggle,
    (estateState: EstateState, triageState: TriageState, hideWithholding: boolean) =>
      buildEstateReceipt(estateState, triageState, editable, hideWithholding),
  );

export const selectBeneByRelationshipReceipt = (editable: boolean = true) =>
  createSelector(
    selectBeneficiaryState,
    selectTriageState,
    selectMfePersonalInfoClientPersonalInfo,
    (
      state: BeneficiariesState,
      guide: TriageState,
      personalInfo: MfePersonalInfoClientState['personalInfo'],
    ) => buildBeneByRelationshipReceipt(null, state, guide, personalInfo, editable),
  );

export const selectAccountFromNonretirementReceipt = (editable: boolean = true) =>
  createSelector(
    selectedAccountsFrom,
    selectAccountType,
    selectTriageState,
    (state, accountType, triageState) =>
      buildAccountFromNonretirement(state, accountType, editable, triageState),
  );

export const selectAccountFromIRAReceipt = (editable: boolean = true) =>
  createSelector(
    selectTriageState,
    selectedClientAccountsState,
    selectIsProspect,
    (guideState, selectedAccount, prospect) =>
      buildAccountFromIRA(guideState, selectedAccount, prospect, editable),
  );

export const selectAccountFromTrustToTrustReceipt = (editable: boolean) =>
  createSelector(selectTCFTrustState, (tcfTrustState) =>
    buildAccountFromTrust(tcfTrustState, editable),
  );

export const selectAccountToIRAReceipt = (editable: boolean) =>
  createSelector(
    selectTriageState,
    selectedClientAccountsState,
    getOpenAccountState,
    selectInheritanceVCDToggle,
    (
      guide: TriageState,
      selectedAccountState: ISelectedClientAccountsState,
      openAccountState: IOpenAccountState,
      VCDToggleOn: boolean,
    ) => buildAccountToIRA(guide, selectedAccountState, openAccountState, editable, VCDToggleOn),
  );

export const selectAccountToTrustToTrustReceipt = () => createSelector(() => buildAccountToTrust());

export const selectAccountToNonretirementReceipt = (editable: boolean = true) =>
  createSelector(
    selectMfePersonalInfoClientPersonalInfo,
    selectedClientAccountsState,
    selectTriageState,
    selectEstateState,
    selectDecedentFullName,
    getOpenAccountState,
    (
      personalInfo: MfePersonalInfoClientState['personalInfo'],
      selectedAccountState: ISelectedClientAccountsState,
      guideState: TriageState,
      estateState: EstateState,
      decedentName: string,
      openAccountState: IOpenAccountState,
    ) => {
      return buildAccountToNonretirement(
        personalInfo,
        selectedAccountState,
        guideState,
        estateState,
        decedentName,
        openAccountState,
        editable,
      );
    },
  );

export const selectAccountToMultiInheritanceReceipt = () =>
  createSelector(
    selectCurrentTransaction,
    selectInheritanceVCDToggle,
    selectMultiInheritanceEstateToggle,
    selectInheritanceIndividualJointToTrustToggle,
    (
      currentTransaction: TransactionState | null,
      VCDToggleOn: boolean,
      multiEstateToggleOn: boolean,
      individualJointToTrustToggleOn: boolean,
    ) => {
      return buildAccountToMultiInheritance(
        currentTransaction,
        VCDToggleOn,
        multiEstateToggleOn,
        individualJointToTrustToggleOn,
      );
    },
  );

export const selectDefaultBeneficiaryReceipt = () =>
  createSelector(
    selectTriageState,
    selectTransactionType,
    selectAccountType,
    (triageState: TriageState, transactionType: TransactionTypesEnum, accountType: AccountTypes) =>
      buildDefaultBeneficiaryReceipt(triageState, transactionType, accountType),
  );

export const selectFundingReceipt = (editable: boolean) =>
  createSelector(
    selectTriageState,
    selectedClientAccountsState,
    getOpenAccountState,
    selectInheritanceVCDToggle,
    (
      triageState: TriageState,
      selectedClientAccountsState: ISelectedClientAccountsState,
      openAccountState: IOpenAccountState,
      VCDToggleOn: boolean,
    ) =>
      buildFundingReceipt(
        triageState,
        selectedClientAccountsState,
        editable,
        openAccountState,
        VCDToggleOn,
      ),
  );

export const getFromAccountData = (editable: boolean) =>
  createSelector(
    selectTriageState,
    selectAccountFromNonretirementReceipt(),
    selectAccountFromIRAReceipt(),
    selectAccountFromTrustToTrustReceipt(editable),
    (triage, from, fromIRA, fromTrustToTrust) => {
      switch (true) {
        case getIsTrustToTrustFlow(triage):
          return fromTrustToTrust;
        case AccountTypes.IRA === triage.accountTypeInheriting:
          return fromIRA;
        default:
          return from;
      }
    },
  );

export const getToAccountData = (editable: boolean) =>
  createSelector(
    selectTriageState,
    selectAccountToNonretirementReceipt(editable),
    selectAccountToIRAReceipt(editable),
    selectAccountToTrustToTrustReceipt(),
    selectAccountToMultiInheritanceReceipt(),
    (triage, to, toIRA, toTrustToTrust, toMultiInheritance) => {
      switch (true) {
        case getIsMultiInheritanceFlow(triage):
          return toMultiInheritance;
        case getIsTrustToTrustFlow(triage):
          return toTrustToTrust;
        case AccountTypes.IRA === triage.accountTypeInheriting:
          return toIRA;
        default:
          return to;
      }
    },
  );

export const getStandardReceiptData = (editable: boolean) =>
  createSelector(
    selectDecedentInfoReceipt(editable),
    selectDocumentUploadReceipt(),
    selectBeneByRelationshipReceipt(),
    getFromAccountData(editable),
    getToAccountData(editable),
    selectEstateReceipt(editable),
    selectProfileReceipt(editable),
    selectFundingReceipt(false),
    selectTrustInfoReceipt(editable),
    selectAddedTrusteeReceipt(editable),
    selectRemovedTrusteeReceipt(editable),
    /* eslint-disable max-params */
    (
      owner,
      documents,
      beneByRelationship,
      from,
      to,
      estate,
      profile,
      funding,
      trustInfo,
      addedTrustee,
      removedTrustee,
    ) => {
      return {
        owner,
        documents,
        beneByRelationship,
        from,
        to,
        trustInfo,
        estate,
        profile,
        addedTrustee,
        removedTrustee,
        funding,
      };
    },
  );

export const getNewAccountReceiptData = createSelector(
  selectEmploymentReceipt(),
  selectFinancialProfileReceipt(),
  selectDefaultBeneficiaryReceipt(),
  (employment, financial, defaultBeneficiary) => {
    return {
      employment,
      financial,
      defaultBeneficiary,
    };
  },
);

export const selectInheritanceReceiptData = (editable: boolean = true) =>
  createSelector(
    getStandardReceiptData(editable),
    getNewAccountReceiptData,
    (standardReceiptData, newAccountReceiptData) => {
      return {
        ...standardReceiptData,
        ...newAccountReceiptData,
      };
    },
  );

export const selectMultiInheritanceNewAccountReceiptData = (editable: boolean = true) =>
  createSelector(
    selectProfileReceipt(editable),
    getToAccountData(editable),
    (profile, accountTo) => {
      return {
        accountTo,
        profile: { ...profile, header: 'Profile' },
      };
    },
  );

export const selectTrustInfoReceipt = (editable: boolean) =>
  createSelector(
    selectTrustState,
    selectTriageState,
    selectTrustTaxInfoState,
    selectTCFTrustState,
    selectInheritanceNewTermsToggle,
    (
      trustInfoState: TrustState,
      triage: TriageState,
      trustTaxInfoState: TrustTaxInfoState,
      tcfTrustState: TCFTrustState,
      hideWithholding: boolean,
    ) =>
      buildTrustInfoReceipt(
        triage,
        trustInfoState,
        trustTaxInfoState,
        tcfTrustState,
        editable,
        hideWithholding,
      ),
  );

export const selectAddedTrusteeReceipt = (editable: boolean) =>
  createSelector(
    selectTriageState,
    selectTCFTrustState,
    (triage: TriageState, tcfTrustState: TCFTrustState) =>
      buildTrusteeReceipt(triage, tcfTrustState, false, editable),
  );

export const selectRemovedTrusteeReceipt = (editable: boolean) =>
  createSelector(
    selectTriageState,
    selectTCFTrustState,
    (triage: TriageState, tcfTrustState: TCFTrustState) =>
      buildTrusteeReceipt(triage, tcfTrustState, true, editable),
  );
